import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, CheckCircle, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const Offshore = () => {
  const benefits = [
    'No physical office required',
    '100% foreign ownership',
    'Complete tax exemption',
    'Asset protection',
    'Confidentiality and privacy',
    'No currency restrictions',
    'Low setup and maintenance costs',
    'Minimal reporting requirements'
  ];

  const useCases = [
    {
      title: 'International Trading',
      desc: 'Conduct cross-border trade without physical presence'
    },
    {
      title: 'Holding Companies',
      desc: 'Hold shares, assets, and intellectual property'
    },
    {
      title: 'Asset Protection',
      desc: 'Protect personal and business assets'
    },
    {
      title: 'Investment Vehicle',
      desc: 'Manage investments and portfolios'
    },
    {
      title: 'Consultancy Services',
      desc: 'Provide international consulting services'
    },
    {
      title: 'E-commerce',
      desc: 'Operate online businesses globally'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="relative py-32 flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(20, 10, 30, 0.8), rgba(20, 10, 30, 0.8)), url(https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-3xl text-white">
            <div className="flex items-center gap-3 mb-4">
              <Globe size={48} className="text-[#DC143C]" />
              <span className="text-2xl font-bold">Offshore Company Setup</span>
            </div>
            <h1 className="text-5xl font-bold mb-6">UAE Offshore Company Formation</h1>
            <p className="text-xl leading-relaxed mb-8">
              Conduct business activities without physical presence in the UAE. Perfect for international trade, asset protection, and global operations.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-[#DC143C] hover:bg-[#DC143C]/90 text-white px-8 py-6 text-lg"
              >
                Setup Offshore Company
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">What is an Offshore Company?</h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              A UAE offshore company is registered in one of the designated offshore jurisdictions such as Jebel Ali (JAFZA) or RAK ICC. These companies can conduct business internationally but cannot trade within the UAE mainland.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Offshore companies are ideal for international businesses that don't require a physical presence in the UAE but want to benefit from the country's stable economy, strategic location, and favorable business environment.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Key Advantages</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="bg-white p-6 rounded-xl shadow-md hover:shadow-xl transition-all">
                <CheckCircle className="text-green-600 mb-3" size={32} />
                <p className="text-gray-800 font-medium">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Perfect For</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {useCases.map((useCase, idx) => (
              <div key={idx} className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-xl border border-gray-200 hover:border-[#6B1B4D] hover:shadow-xl transition-all">
                <Shield className="text-[#6B1B4D] mb-4" size={40} />
                <h3 className="text-xl font-bold text-gray-800 mb-3">{useCase.title}</h3>
                <p className="text-gray-600">{useCase.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-800 mb-12 text-center">Setup Requirements</h2>
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#6B1B4D] flex-shrink-0 mt-1" size={24} />
                  <span className="text-gray-700 text-lg">Passport copies of shareholders and directors</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#6B1B4D] flex-shrink-0 mt-1" size={24} />
                  <span className="text-gray-700 text-lg">Proof of residential address</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#6B1B4D] flex-shrink-0 mt-1" size={24} />
                  <span className="text-gray-700 text-lg">Bank reference letter</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#6B1B4D] flex-shrink-0 mt-1" size={24} />
                  <span className="text-gray-700 text-lg">Business plan (for certain activities)</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#6B1B4D] flex-shrink-0 mt-1" size={24} />
                  <span className="text-gray-700 text-lg">Minimum of 1 shareholder required</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-[#0A1929] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Starting from AED 8,500</h2>
          <p className="text-xl mb-8">Complete offshore company setup with all documentation</p>
          <Link to="/contact">
            <Button 
              size="lg" 
              className="bg-[#DC143C] hover:bg-[#DC143C]/90 text-white px-8 py-6 text-lg"
            >
              Get Started
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Offshore;